// Piezas pequeñas que comparten las pantallas de V360, armadas con @iavmx/ui.
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { Boton, cn, Pill, Semaforo } from "@iavmx/ui";
import { COLOR_SEMAFORO, ETIQUETA_SEMAFORO, type Semaforo as TipoSemaforo } from "./util";

/** Chip de filtro: tinta cuando está activo, contorno cuando no. */
export function Chip({
  activo,
  children,
  onClick,
  n,
}: {
  activo?: boolean;
  children: React.ReactNode;
  onClick: () => void;
  n?: number;
}) {
  return (
    <Boton
      type="button"
      variante={activo ? "primario" : "contorno"}
      tamano="sm"
      onClick={onClick}
      aria-pressed={activo}
      className="h-11 md:h-8"
    >
      {children}
      {n !== undefined && <span className={cn("font-bold", activo ? "" : "text-tinta-tenue")}>{n}</span>}
    </Boton>
  );
}

export function Sem({ semaforo, corto }: { semaforo: TipoSemaforo; corto?: boolean }) {
  return <Semaforo color={COLOR_SEMAFORO[semaforo]}>{corto ? null : ETIQUETA_SEMAFORO[semaforo]}</Semaforo>;
}

/** Marca "Falta" junto a un campo del brief mínimo. */
export function Falta() {
  return <Pill tono="ambar">Falta</Pill>;
}

/** Migas: Campañas / Aniversario Vanguardia. */
export function Migas({ partes }: { partes: { etiqueta: string; to?: string }[] }) {
  return (
    <nav aria-label="Ubicación" className="mb-3 flex flex-wrap items-center gap-1 font-ui text-sm text-tinta-tenue">
      {partes.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && <ChevronRight className="size-3.5" aria-hidden="true" />}
          {p.to ? (
            <Link to={p.to} className="text-azul hover:underline">
              {p.etiqueta}
            </Link>
          ) : (
            <span className="text-tinta">{p.etiqueta}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

/** Etiqueta versalita + valor, para las fichas. */
export function Dato({ etiqueta, children, className }: { etiqueta: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("min-w-0", className)}>
      <p className="font-ui text-[11px] font-bold uppercase tracking-wide text-tinta-tenue">{etiqueta}</p>
      <div className="mt-0.5 font-ui text-sm text-tinta">{children}</div>
    </div>
  );
}

/** Aviso en panel (sin IA, incompleta, pendiente de aprobación). */
export function Nota({ tono = "neutro", children, className }: { tono?: "neutro" | "ambar" | "rojo" | "azul"; children: React.ReactNode; className?: string }) {
  const borde = { neutro: "border-linea-fuerte", ambar: "border-ambar", rojo: "border-rojo", azul: "border-azul" }[tono];
  return (
    <div role="status" className={cn("border-l-2 bg-fondo-suave px-4 py-3 font-ui text-sm text-tinta-2", borde, className)}>
      {children}
    </div>
  );
}

export function EncabezadoSeccion({ children, accion }: { children: React.ReactNode; accion?: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-end justify-between gap-3">
      <h2 className="font-titulo text-xl font-bold">{children}</h2>
      {accion}
    </div>
  );
}
